import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Calendar, 
  BookOpen, 
  Users, 
  Clock,
  FileText,
  Upload,
  Download,
  XCircle
} from 'lucide-react';
import Layout from '../components/Layout';
import { useApp } from '../contexts/AppContext';
import { HomeworkAssignment, Class } from '../types';
import { homeworkService, homeworkSubmissionService } from '../services/apiService';
import toast from 'react-hot-toast';

const HomeworkPage: React.FC = () => {
  const { classes } = useApp();
  const [assignments, setAssignments] = useState<HomeworkAssignment[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState<HomeworkAssignment | null>(null);
  const [selectedClass, setSelectedClass] = useState<string>('all');
  const [showSubmissionsModal, setShowSubmissionsModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<HomeworkAssignment | null>(null);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [showGradingModal, setShowGradingModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);
  const [gradingData, setGradingData] = useState({
    points: 0,
    feedback: ''
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    classId: '',
    dueDate: '',
    dueTime: '',
    maxPoints: 100,
    instructions: '',
    attachments: [] as File[]
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAssignments();
  }, []);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const data = await homeworkService.getAll();
      setAssignments(data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading assignments:', error);
      toast.error('Failed to load assignments');
      setLoading(false);
    }
  };

  const filteredAssignments = assignments.filter(assignment => {
    if (selectedClass === 'all') return true;
    return assignment.classId === selectedClass;
  });

  const loadSubmissions = async (assignmentId: string) => {
    try {
      const submissionsData = await homeworkSubmissionService.getForAssignment(assignmentId);
      setSubmissions(submissionsData);
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast.error('Failed to load submissions');
      setSubmissions([]);
    }
  };

  const handleViewSubmissions = async (assignment: HomeworkAssignment) => {
    setSelectedAssignment(assignment);
    setShowSubmissionsModal(true);
    await loadSubmissions(assignment.id);
  };

  const handleGradeSubmission = (submission: any) => {
    setSelectedSubmission(submission);
    setGradingData({
      points: submission.points || 0,
      feedback: submission.feedback || ''
    });
    setShowGradingModal(true);
  };

  const handleSubmitGrade = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSubmission) return;

    try {
      await homeworkSubmissionService.grade(
        selectedSubmission.id,
        gradingData.points,
        gradingData.feedback
      );
      toast.success('Submission graded successfully!');
      setShowGradingModal(false);
      setSelectedSubmission(null);
      setGradingData({ points: 0, feedback: '' });
      // Reload submissions
      if (selectedAssignment) {
        await loadSubmissions(selectedAssignment.id);
      }
    } catch (error) {
      console.error('Error grading submission:', error);
      toast.error('Failed to grade submission');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.classId || !formData.dueDate) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const dueDateTime = new Date(`${formData.dueDate}T${formData.dueTime || '23:59'}`);
      
      if (editingAssignment) {
        await homeworkService.update(editingAssignment.id, {
          title: formData.title,
          description: formData.description,
          classId: formData.classId,
          dueDate: dueDateTime.toISOString(),
          maxPoints: formData.maxPoints,
          instructions: formData.instructions,
        } as any);
        toast.success('Assignment updated successfully');
      } else {
        await homeworkService.create({
          title: formData.title,
          classId: formData.classId,
          dueDate: dueDateTime.toISOString(),
          description: formData.description,
          maxPoints: formData.maxPoints,
          instructions: formData.instructions,
        });
        toast.success('Assignment created successfully');
      }
      
      setShowModal(false);
      setEditingAssignment(null);
      resetForm();
      loadAssignments();
    } catch (error) {
      console.error('Error saving assignment:', error);
      toast.error('Failed to save assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (assignment: HomeworkAssignment) => {
    setEditingAssignment(assignment);
    const dueDate = new Date(assignment.dueDate);
    setFormData({
      title: assignment.title,
      description: assignment.description || '',
      classId: assignment.classId,
      dueDate: dueDate.toISOString().split('T')[0],
      dueTime: dueDate.toTimeString().slice(0, 5),
      maxPoints: assignment.maxPoints,
      instructions: assignment.instructions || '',
      attachments: []
    });
    setShowModal(true);
  };

  const handleDelete = async (assignment: HomeworkAssignment) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return;
    }

    try {
      await homeworkService.delete(assignment.id);
      toast.success('Assignment deleted successfully');
      loadAssignments();
    } catch (error) {
      console.error('Error deleting assignment:', error);
      toast.error('Failed to delete assignment');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      classId: '',
      dueDate: '',
      dueTime: '',
      maxPoints: 100,
      instructions: '',
      attachments: []
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormData(prev => ({
        ...prev,
        attachments: Array.from(e.target.files || [])
      }));
    }
  };

  const getClassName = (classId: string) => {
    const classObj = classes.find(c => c.id === classId);
    return classObj?.name || 'Unknown Class';
  };

  const downloadAttachment = (dataUrl: string, fileName: string) => {
    try {
      // Convert base64 data URL to blob
      fetch(dataUrl)
        .then(res => res.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        })
        .catch(err => {
          console.error('Error downloading file:', err);
          toast.error('Failed to download file');
        });
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const getStatusColor = (assignment: HomeworkAssignment) => {
    const now = new Date();
    const dueDate = new Date(assignment.dueDate);
    
    if (dueDate < now) {
      return 'text-red-600 bg-red-100';
    } else if (dueDate.getTime() - now.getTime() < 24 * 60 * 60 * 1000) {
      return 'text-yellow-600 bg-yellow-100';
    } else {
      return 'text-green-600 bg-green-100';
    }
  };

  const getStatusText = (assignment: HomeworkAssignment) => {
    const now = new Date();
    const dueDate = new Date(assignment.dueDate);
    
    if (dueDate < now) {
      return 'Overdue';
    } else if (dueDate.getTime() - now.getTime() < 24 * 60 * 60 * 1000) {
      return 'Due Soon';
    } else {
      return 'Active';
    }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Homework Assignments
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Create and manage homework assignments for your classes
            </p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Assignment
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <label htmlFor="class-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Filter by Class
              </label>
              <select
                id="class-filter"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">All Classes</option>
                {classes.map((classItem) => (
                  <option key={classItem.id} value={classItem.id}>
                    {classItem.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Assignments List */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 sm:p-6">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Loading assignments...</p>
            </div>
          ) : filteredAssignments.length === 0 ? (
            <div className="p-8 text-center">
              <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                No assignments
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Get started by creating a new homework assignment.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => setShowModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Assignment
                </button>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAssignments.map((assignment) => (
                <div key={assignment.id} className="py-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {assignment.title}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(assignment)}`}>
                          {getStatusText(assignment)}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center">
                          <Users className="w-4 h-4 mr-1" />
                          {getClassName(assignment.classId)}
                        </div>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          Due: {new Date(assignment.dueDate).toLocaleDateString()}
                        </div>
                        <div className="flex items-center">
                          <FileText className="w-4 h-4 mr-1" />
                          {assignment.maxPoints} points
                        </div>
                      </div>
                      {assignment.description && (
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                          {assignment.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleViewSubmissions(assignment)}
                        className="p-2 text-blue-400 hover:text-blue-600 dark:hover:text-blue-300"
                        title="View Submissions"
                      >
                        <Users className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleEdit(assignment)}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(assignment)}
                        className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="w-full max-w-xl mx-4 p-5 border shadow-lg rounded-lg bg-white dark:bg-gray-800">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  {editingAssignment ? 'Edit Assignment' : 'Create New Assignment'}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Class *
                    </label>
                    <select
                      value={formData.classId}
                      onChange={(e) => setFormData(prev => ({ ...prev, classId: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      required
                    >
                      <option value="">Select a class</option>
                      {classes.map((classItem) => (
                        <option key={classItem.id} value={classItem.id}>
                          {classItem.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Due Date *
                      </label>
                      <input
                        type="date"
                        value={formData.dueDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                        className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Due Time
                      </label>
                      <input
                        type="time"
                        value={formData.dueTime}
                        onChange={(e) => setFormData(prev => ({ ...prev, dueTime: e.target.value }))}
                        className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Max Points
                    </label>
                    <input
                      type="number"
                      value={formData.maxPoints}
                      onChange={(e) => setFormData(prev => ({ ...prev, maxPoints: parseInt(e.target.value) || 100 }))}
                      className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Instructions
                    </label>
                    <textarea
                      value={formData.instructions}
                      onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                      rows={3}
                      className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Detailed instructions for students..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Attachments
                    </label>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="mt-1 block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setEditingAssignment(null);
                        resetForm();
                      }}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      {loading ? 'Saving...' : (editingAssignment ? 'Update' : 'Create')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Submissions Modal */}
        {showSubmissionsModal && selectedAssignment && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Submissions for: {selectedAssignment.title}
                  </h3>
                  <button
                    onClick={() => setShowSubmissionsModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
                
                {submissions.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                      No submissions yet
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Students haven't submitted this assignment yet.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {submissions.map((submission) => (
                      <div key={submission.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                {submission.firstName} {submission.lastName}
                              </h4>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                submission.status === 'graded' ? 'bg-green-100 text-green-800' :
                                submission.status === 'late' ? 'bg-red-100 text-red-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {submission.status === 'graded' ? 'Graded' :
                                 submission.status === 'late' ? 'Late' : 'Submitted'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              Submitted: {new Date(submission.submittedAt).toLocaleDateString()}
                            </p>
                            <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                              <p className="text-sm text-gray-700 dark:text-gray-300">
                                {submission.content}
                              </p>
                            </div>
                            
                                {/* Show attachments if any */}
                                <div className="mt-2">
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    <Download className="w-3 h-3 mr-1 inline" />
                                    Attachments:
                                  </div>
                                  <div className="space-y-1">
                                    {(() => {
                                      try {
                                        // Handle both string and array formats
                                        let parsedAttachments;
                                        if (typeof submission.attachments === 'string') {
                                          parsedAttachments = JSON.parse(submission.attachments || '[]');
                                        } else if (Array.isArray(submission.attachments)) {
                                          parsedAttachments = submission.attachments;
                                        } else {
                                          parsedAttachments = [];
                                        }
                                        
                                        if (Array.isArray(parsedAttachments) && parsedAttachments.length > 0) {
                                          return parsedAttachments.map((attachment: any, index: number) => {
                                            const fileName = attachment.originalName || attachment.fileName || `Attachment ${index + 1}`;
                                            const attachmentData = attachment.data || attachment.dataUrl || '';
                                            const isImage = fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/);
                                            
                                            return (
                                              <div key={index} className="flex items-center space-x-2">
                                                {isImage && attachmentData ? (
                                                  <div>
                                                    <img 
                                                      src={attachmentData} 
                                                      alt={fileName}
                                                      className="max-w-xs max-h-48 rounded"
                                                    />
                                                    <button
                                                      onClick={() => downloadAttachment(attachmentData, fileName)}
                                                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-xs flex items-center mt-1 cursor-pointer"
                                                    >
                                                      <Download className="w-3 h-3 mr-1" />
                                                      {fileName}
                                                    </button>
                                                  </div>
                                                ) : (
                                                  <button
                                                    onClick={() => attachmentData && downloadAttachment(attachmentData, fileName)}
                                                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-xs flex items-center cursor-pointer"
                                                  >
                                                    <Download className="w-3 h-3 mr-1" />
                                                    {fileName}
                                                  </button>
                                                )}
                                              </div>
                                            );
                                          });
                                        }
                                        return <span className="text-xs text-gray-500">No attachments</span>;
                                      } catch (error) {
                                        console.error('Error parsing attachments:', error, submission.attachments);
                                        return <span className="text-xs text-gray-500">Error loading attachments</span>;
                                      }
                                    })()}
                                  </div>
                                </div>
                            
                            {submission.points !== null && (
                              <div className="mt-2 flex items-center space-x-4 text-sm">
                                <span className="text-gray-600 dark:text-gray-400">
                                  Points: <strong>{submission.points}/{selectedAssignment.maxPoints}</strong>
                                </span>
                                {submission.feedback && (
                                  <span className="text-gray-600 dark:text-gray-400">
                                    Feedback: {submission.feedback}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            {submission.status !== 'graded' && (
                              <button
                                onClick={() => handleGradeSubmission(submission)}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                Grade
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Grading Modal */}
        {showGradingModal && selectedSubmission && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Grade Submission
                  </h3>
                  <button
                    onClick={() => setShowGradingModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
                
                <form onSubmit={handleSubmitGrade} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Student: {selectedSubmission.firstName} {selectedSubmission.lastName}
                    </label>
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {selectedSubmission.content}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Points (out of {selectedAssignment?.maxPoints})
                    </label>
                    <input
                      type="number"
                      value={gradingData.points}
                      onChange={(e) => setGradingData(prev => ({ ...prev, points: parseInt(e.target.value) || 0 }))}
                      className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      min="0"
                      max={selectedAssignment?.maxPoints}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Feedback
                    </label>
                    <textarea
                      value={gradingData.feedback}
                      onChange={(e) => setGradingData(prev => ({ ...prev, feedback: e.target.value }))}
                      rows={4}
                      className="mt-1 block w-full border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Provide feedback for the student..."
                    />
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowGradingModal(false)}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      Submit Grade
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HomeworkPage;
